import { useState } from "react";
import type { SyncConflict, ConflictResolution } from "@roqeet/core";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";

interface SyncConflictModalProps {
  conflicts: SyncConflict[];
  onResolve: (resolutions: Map<string, ConflictResolution>) => void;
  onCancel: () => void;
}

export function SyncConflictModal({ conflicts, onResolve, onCancel }: SyncConflictModalProps) {
  const { t } = useT();
  useEscapeToClose(onCancel);
  const [resolutions, setResolutions] = useState<Map<string, ConflictResolution>>(() => {
    // 기본값: 각 충돌의 제안값
    return new Map(conflicts.map((c) => [c.id, c.suggestion]));
  });

  const handleSelect = (id: string, res: ConflictResolution) => {
    const next = new Map(resolutions);
    next.set(id, res);
    setResolutions(next);
  };

  const handleApply = () => {
    onResolve(resolutions);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col rounded-2xl border border-line bg-bg shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">
            {t("sync.conflictTitle", { count: conflicts.length })}
          </h2>
          <button
            className="text-ink-faint hover:text-ink"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
          <p className="text-xs text-ink-faint">
            {t("sync.conflictDesc")}
          </p>

          {conflicts.map((c) => {
            const currentRes = resolutions.get(c.id) ?? c.suggestion;
            
            // 이름이나 제목 등 식별 가능한 필드 추출 시도
            const localPayload = c.local.payload as Record<string, unknown> | null;
            const remotePayload = c.remote.payload as Record<string, unknown> | null;
            
            const localName = String(localPayload?.name || localPayload?.title || localPayload?.handle || c.id);
            const remoteName = String(remotePayload?.name || remotePayload?.title || remotePayload?.handle || c.id);

            const isDeletedLocal = !!c.local.deleted;
            const isDeletedRemote = !!c.remote.deleted;

            return (
              <div key={c.id} className="rounded-xl border border-line bg-bg-soft p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                    {c.table}
                  </span>
                </div>
                
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div className="rounded-lg border border-line p-2 text-xs">
                    <div className="mb-1 font-semibold text-ink">로컬 (내 기기)</div>
                    <div className="text-ink-soft">
                      {isDeletedLocal ? <span className="text-red-400">삭제됨</span> : localName}
                    </div>
                  </div>
                  <div className="rounded-lg border border-line p-2 text-xs">
                    <div className="mb-1 font-semibold text-ink">클라우드 (구글 드라이브)</div>
                    <div className="text-ink-soft">
                      {isDeletedRemote ? <span className="text-red-400">삭제됨</span> : remoteName}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-line bg-bg px-3 py-2 text-xs text-ink-soft hover:bg-bg-raised has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent">
                    <input
                      type="radio"
                      name={`resolve-${c.id}`}
                      className="hidden"
                      checked={currentRes === "keep-local"}
                      onChange={() => handleSelect(c.id, "keep-local")}
                    />
                    로컬 유지
                  </label>
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-line bg-bg px-3 py-2 text-xs text-ink-soft hover:bg-bg-raised has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent">
                    <input
                      type="radio"
                      name={`resolve-${c.id}`}
                      className="hidden"
                      checked={currentRes === "keep-remote"}
                      onChange={() => handleSelect(c.id, "keep-remote")}
                    />
                    클라우드 덮어쓰기
                  </label>
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-line bg-bg px-3 py-2 text-xs text-ink-soft hover:bg-bg-raised has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent">
                    <input
                      type="radio"
                      name={`resolve-${c.id}`}
                      className="hidden"
                      checked={currentRes === "keep-both"}
                      onChange={() => handleSelect(c.id, "keep-both")}
                    />
                    둘 다 보관
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
          <button
            className="rounded-lg px-4 py-2 text-sm font-medium text-ink-soft hover:text-ink"
            onClick={onCancel}
          >
            {t("common.cancel")}
          </button>
          <button
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-bg hover:bg-accent-hover"
            onClick={handleApply}
          >
            {t("common.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
